<div class="home-page">
    <div class="d-flex w-100 align-items-center home-header gap-2 pe-3">
        @if (conversationSummaries.length > 1) {
        <dx-button icon="fa fa-bars"
                   [elementAttr]="{ id: 'conversation-menu-button' }"
                   [disabled]="isDisabled || isConversationPopoverVisible"
                   hint="Show conversations"
                   stylingMode="text"
                   (onClick)="showConversationPopover()">
        </dx-button>

        <dx-popover target="#conversation-menu-button"
                    [visible]="isConversationPopoverVisible"
                    [hideOnOutsideClick]="true"
                    [showCloseButton]="true"
                    [width]="240"
                    [position]="{ of: '#conversation-menu-button', my: 'left top', at: 'left bottom' }"
                    (onHidden)="hideConversationPopover()">
            <div *dxTemplate="let data of 'content'"
                 class="conversation-popover">
                <dx-list [dataSource]="groupedConversationSummaries"
                         [grouped]="true"
                         groupTemplate="conversationGroupTemplate"
                         itemTemplate="conversationItemTemplate"
                         (onItemClick)="switchConversation($event)">
                    <div *dxTemplate="let group of 'conversationGroupTemplate'"
                         class="conversation-group-header">
                        {{ group.key }}
                    </div>

                    <div *dxTemplate="let item of 'conversationItemTemplate'"
                         class="conversation-list-item">
                        <div class="conversation-list-subject"
                             [ngClass]="{'active': item.conversationId === conversationId}">{{ item.subject }}</div>
                        <div class="conversation-list-timestamp">{{ formatConversationSummaryTime(item.timestamp) }}</div>
                        @if (item.conversationId !== conversationId) {
                        <dx-button icon="fa fa-trash"
                                   stylingMode="text"
                                   type="normal"
                                   hint="Delete conversation"
                                   class="conversation-delete-button"
                                   (onClick)="deleteConversation(item.conversationId, $event)">
                        </dx-button>
                        } @else {
                        <span class="blank-delete"></span>
                        }
                    </div>
                </dx-list>
            </div>
        </dx-popover>
        }
        @if (isDisabled) {
        <div class="d-flex gap-3 align-items-center">
            <dx-button text="Interrupt Response"
                       type="success"
                       stylingMode="outlined"
                       (onClick)="cancelResponse()">
            </dx-button>
            <dx-load-indicator height="20"
                               width="20"></dx-load-indicator>
        </div>
        } @else {
        <div class="d-flex gap-2 align-items-center">
            <dx-button text="New Chat"
                       type="default"
                       stylingMode="text"
                       (onClick)="startNewChat()">
            </dx-button>
            <span class="small text-muted logged-in">Logged in as {{ loggedInEmail }}</span>
            <div id="openai-api-key-button"
                 class="api-key-trigger"
                 role="button"
                 tabindex="0"
                 aria-label="Show OpenAI-compatible API key"
                 [attr.aria-expanded]="isApiKeyPopoverVisible ? 'true' : 'false'"
                 (click)="showApiKeyPopover()"
                 (keydown.enter)="showApiKeyPopover()"
                 (keydown.space)="showApiKeyPopover(); $event.preventDefault()">
                <i class="fa fa-key"></i>
            </div>

            <dx-popover target="#openai-api-key-button"
                        [visible]="isApiKeyPopoverVisible"
                        [hideOnOutsideClick]="true"
                        [showCloseButton]="false"
                        [width]="380"
                        [position]="{ of: '#openai-api-key-button', my: 'left top', at: 'left bottom' }"
                        (onHidden)="hideApiKeyPopover()">
                <div *dxTemplate="let data of 'content'"
                     class="api-key-popover">
                    <div class="api-key-popover-title">OpenAI-compatible API key</div>
                    @if (isOpenAiApiKeyLoading) {
                    <div class="api-key-popover-loading">
                        <dx-load-indicator height="20"
                                           width="20"></dx-load-indicator>
                        <span>Loading key...</span>
                    </div>
                    } @else if (apiKey) {
                    <div class="api-key-value">{{ apiKey }}</div>
                    <div class="d-flex flex-column gap-1">
                        @if (openAiApiKeyBaseUrl) {
                        <div class="api-key-base-url">OpenAI Base URL: {{ openAiApiKeyBaseUrl }}</div>
                        }
                        @if (anthropicApiKeyBaseUrl) {
                        <div class="api-key-base-url">Anthropic Base URL: {{ anthropicApiKeyBaseUrl }}</div>
                        }
                    </div>
                    } @else {
                    <div class="api-key-error">{{ openAiApiKeyError || 'Unable to load the API key.' }}</div>
                    }

                    <div class="api-key-actions">
                        <dx-button [text]="isOpenAiApiKeyCopied ? 'Copied' : 'Copy'"
                                   icon="copy"
                                   type="default"
                                   stylingMode="contained"
                                   [disabled]="!apiKey"
                                   (onClick)="copyOpenAiApiKey()">
                        </dx-button>
                        <dx-button text="Close"
                                   stylingMode="text"
                                   (onClick)="hideApiKeyPopover()">
                        </dx-button>
                    </div>
                </div>
            </dx-popover>
        </div>
        }

        <dx-button text="Logout"
                   [disabled]="isDisabled"
                   class="ms-auto"
                   type="danger"
                   stylingMode="outlined"
                   (onClick)="logout()"></dx-button>
    </div>

    <div class="chat-container">
        <dx-chat [dataSource]="dataSource"
                 [reloadOnChange]="false"
                 [class.chat-disabled]="isDisabled"
                 [user]="chatUser"
                 [showUserName]="true"
                 [showAvatar]="false"
                 [alerts]="(alerts$ | async) ?? []"
                 (onMessageEntered)="onMessageEntered($event)"
                 messageTemplate="messageTemplate">
            <dxo-chat-file-uploader-options [uploadUrl]="fileUploadUrl"
                                            [uploadHeaders]="fileUploadHeaders"
                                            [name]="'file'"
                                            [maxFileSize]="10485760"
                                            [allowedFileExtensions]="['.jpg', '.jpeg', '.png', '.gif', '.webp', '.pdf', '.txt', '.md', '.csv', '.json']"
                                            [onUploaded]="handleFileUploaded">
            </dxo-chat-file-uploader-options>
            <div *dxTemplate="let data of 'messageTemplate'">
                @if (data.message.progressType) {
                @if (data.message.progressType === ProgressType.FunctionCall) {
                <div class="d-flex flex-column gap-1 align-items-start">
                    <div class="d-flex flex-row gap-1 align-items-center">
                        <i class="fa fa-cogs fa-spin"
                           height="20"
                           width="20"></i>
                        <span class="small">{{data.message.text}}...</span>
                    </div>
                    @if (data.message.renderedMessage) {
                    <div class="function-call-args"
                         [innerHTML]="data.message.renderedMessage"></div>
                    }
                </div>
                } @else {
                <div class="d-flex flex-row gap-1 align-items-center">
                    <dx-load-indicator height="20"
                                       width="20"></dx-load-indicator>
                    {{data.message.text}}
                </div>
                }
                } @else {
                <div class="d-flex flex-column gap-3">
                    @let isAssistantMessage = data.message.author.id !== chatUser.id;
                    @if (!data.message.completed && isAssistantMessage) {
                    <div class="d-flex flex-row gap-1 align-items-center w-100">
                        <i class="fa fa-spinner fa-spin"
                           height="20"
                           width="20"></i>
                        <span class="small">Streaming response...</span>
                    </div>
                    }

                    <div [innerHTML]="data.message.renderedMessage ?? convertToHtml(data.message)"></div>
                </div>
                }
            </div>
        </dx-chat>
    </div>
</div>

<dx-popup [visible]="isDeleteConversationPopupVisible"
          [showTitle]="true"
          title="Delete conversation"
          [dragEnabled]="false"
          [hideOnOutsideClick]="false"
          [showCloseButton]="true"
          [width]="320"
          height="auto"
          (onHidden)="cancelDeleteConversation()">
    <div *dxTemplate="let data of 'content'">
        <div class="delete-conversation-dialog d-flex flex-column gap-3">
            <div>Delete this conversation? This cannot be undone.</div>

            <div class="d-flex justify-content-end gap-2">
                <dx-button text="Cancel"
                           stylingMode="text"
                           (onClick)="cancelDeleteConversation()">
                </dx-button>
                <dx-button text="Delete"
                           type="danger"
                           stylingMode="contained"
                           (onClick)="confirmDeleteConversation()">
                </dx-button>
            </div>
        </div>
    </div>
</dx-popup>

<ng-template #extraButtons>
    <div class="dx-item dx-toolbar-item dx-toolbar-button"
         role="presentation">
        <div class="dx-item-content dx-toolbar-item-content">
            <div role="button"
                 [attr.aria-label]="isVoiceModeEnabled ? 'Disable voice mode' : 'Enable voice mode'"
                 [attr.aria-pressed]="isVoiceModeEnabled ? 'true' : 'false'"
                 class="dx-widget dx-button dx-button-mode-outlined dx-button-has-text"
                 [class.dx-button-mode-contained]="isVoiceModeEnabled"
                 [class.dx-button-mode-text]="!isVoiceModeEnabled"
                 [class.dx-state-active]="isVoiceModeEnabled"
                 tabindex="0"
                 [attr.aria-disabled]="isDisabled ? 'true' : 'false'"
                 [class.dx-state-disabled]="isDisabled"
                 (click)="!isDisabled && toggleVoiceMode()"
                 (keydown.enter)="!isDisabled && toggleVoiceMode()"
                 (keydown.space)="!isDisabled && toggleVoiceMode(); $event.preventDefault()">
                <div class="dx-button-content">
                    <span class="dx-button-text fa"
                          [ngClass]="voiceModeIcon"></span>
                </div>
            </div>
        </div>
    </div>
</ng-template>
